import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Typography, Descriptions, Spin, Alert, Statistic,
  Row, Col, Button, Table, Tabs, Tag, Badge, Space, Select,
  message, Modal, Form, DatePicker, Timeline, Empty
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, StopOutlined, CheckCircleOutlined,
  AppstoreOutlined, HistoryOutlined, NotificationOutlined, TrophyOutlined,
  BarChartOutlined, FileTextOutlined, ContactsOutlined, CheckSquareOutlined,
  ScheduleOutlined, QuestionCircleOutlined, DatabaseOutlined, TeamOutlined,
  VideoCameraOutlined, RobotOutlined,
  ClockCircleOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { schoolAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import SchoolAdministratorSettings from './SchoolAdministratorSettings';
import dayjs from 'dayjs';
import { formatAreaText } from '../../utils/areaHelpers';

const { Title, Text } = Typography;
const { Option } = Select;

// 状态配置
const STATUS_CONFIG = {
  active:   { label: '正常运营', color: 'success',   badge: 'success',   icon: <CheckCircleOutlined /> },
  inactive: { label: '已停用',   color: 'error',     badge: 'error',     icon: <StopOutlined /> },
  trial:    { label: '试用中',   color: 'processing', badge: 'processing',icon: <ClockCircleOutlined /> },
  expired:  { label: '已到期',   color: 'warning',   badge: 'warning',   icon: <ExclamationCircleOutlined /> },
};

const PLAN_CONFIG = {
  free:       { label: '免费版',   color: 'default' },
  basic:      { label: '基础版',   color: 'blue' },
  pro:        { label: '专业版',   color: 'purple' },
  enterprise: { label: '企业版',   color: 'gold' },
};

// 模块图标映射
const MODULE_ICON_MAP = {
  broadcast:          <NotificationOutlined />,
  student_points:     <TrophyOutlined />,
  student_grades:     <BarChartOutlined />,
  homework:           <FileTextOutlined />,
  contacts:           <ContactsOutlined />,
  attendance:         <CheckSquareOutlined />,
  scheduled_broadcast:<ScheduleOutlined />,
  quiz:               <QuestionCircleOutlined />,
  data_collection:    <DatabaseOutlined />,
  parent_binding:     <TeamOutlined />,
  video_attendance:   <VideoCameraOutlined />,
  ai_assistant:       <RobotOutlined />,
};

const INTERNAL_TEST_MODULES = [
  { module_code: 'broadcast', module_name: '广播通知', category: 'basic', description: '文字、图片、语音广播和定时广播' },
  { module_code: 'contacts', module_name: '通讯录', category: 'basic', description: '学校、班级、教师、学生和家长基础资料' },
  { module_code: 'parent_binding', module_name: '家长绑定', category: 'basic', description: '班级二维码、学生家长关系和绑定核查' },
  { module_code: 'attendance', module_name: '考勤管理', category: 'basic', description: '手动考勤、请假、迟到和缺勤统计' },
  { module_code: 'student_points', module_name: '学生积分', category: 'advanced', description: '积分模板、奖扣分、排行和成长记录' },
  { module_code: 'student_grades', module_name: '成绩管理', category: 'advanced', description: '成绩导入、查询、统计和家长可见结果' },
  { module_code: 'homework', module_name: '作业管理', category: 'advanced', description: '作业发布、回执、反馈和统计' },
  { module_code: 'scheduled_broadcast', module_name: '定时广播', category: 'advanced', description: '定时任务、执行记录和补发处理' },
  { module_code: 'quiz', module_name: '课堂答题', category: 'advanced', description: '题库、在线答题和桌面端答题控制' },
  { module_code: 'data_collection', module_name: '数据收集', category: 'advanced', description: '表单发布、结果汇总和导出' },
  { module_code: 'video_attendance', module_name: '智能考勤', category: 'ai', description: '摄像头、识别结果和考勤联动' },
  { module_code: 'ai_assistant', module_name: 'AI 助手', category: 'ai', description: '批改、生成、分析和学校 AI 配置' },
];

const CATEGORY_LABELS = {
  basic:    { label: '基础功能', color: '#e6f7ff', border: '#91d5ff', text: '#1677ff' },
  advanced: { label: '高级功能', color: '#fff7e6', border: '#ffd591', text: '#fa8c16' },
  ai:       { label: 'AI功能',   color: '#f9f0ff', border: '#d3adf7', text: '#722ed1' },
};

const SchoolDetail = () => {
  const { isSuperAdmin } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'info';

  const [school, setSchool] = useState(null);
  const [stats, setStats] = useState(null);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(defaultTab);

  // 状态变更Modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusForm] = Form.useForm();

  const fetchSchoolDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [schoolRes, statsRes, classesRes, teachersRes] = await Promise.allSettled([
        schoolAPI.getById(id),
        schoolAPI.getStats(id),
        schoolAPI.getClasses(id),
        schoolAPI.getTeachers(id),
      ]);

      if (schoolRes.status === 'fulfilled' && schoolRes.value.data.success) {
        setSchool(schoolRes.value.data.data);
      } else {
        setError('获取学校信息失败');
        return;
      }
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data.data);
      if (classesRes.status === 'fulfilled') setClasses(classesRes.value.data.data || []);
      if (teachersRes.status === 'fulfilled') setTeachers(teachersRes.value.data.data || []);
    } catch (err) {
      setError('获取学校详情失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await schoolAPI.getOperationLogs(id, { pageSize: 50 });
      if (res.data.success) setLogs(res.data.data || []);
    } catch (e) { /* ignore */ }
  }, [id]);

  useEffect(() => { fetchSchoolDetails(); }, [fetchSchoolDetails]);
  useEffect(() => {
    if (activeTab === 'logs') fetchLogs();
  }, [activeTab, fetchLogs]);

  // 学校状态变更
  const handleStatusChange = async (values) => {
    try {
      await schoolAPI.toggleStatus(id, values.status,
        values.expire_date ? values.expire_date.format('YYYY-MM-DD') : null);
      message.success(`学校状态已更新为: ${STATUS_CONFIG[values.status]?.label}`);
      setStatusModalOpen(false);
      fetchSchoolDetails();
    } catch (e) {
      message.error('状态更新失败');
    }
  };

  // 班级表格列
  const classColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '班级名称', dataIndex: 'name', key: 'name' },
    { title: '年级', dataIndex: 'grade', key: 'grade', render: t => t || '-' },
    {
      title: '状态', dataIndex: 'status', key: 'status',
      render: s => <Badge status={s === 'active' ? 'success' : 'default'} text={s === 'active' ? '在线' : '离线'} />
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at',
      render: t => t ? dayjs(t).format('YYYY-MM-DD') : '-' },
  ];

  // 教师表格列
  const teacherColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '姓名', dataIndex: 'name', key: 'name' },
    { title: '学科', dataIndex: 'subject', key: 'subject', render: t => t || '-' },
    {
      title: '状态', dataIndex: 'status', key: 'status',
      render: s => <Badge status={s === 'active' ? 'success' : 'error'} text={s === 'active' ? '正常' : '停用'} />
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at',
      render: t => t ? dayjs(t).format('YYYY-MM-DD') : '-' },
  ];

  const operationTypeLabels = {
    school_create: '创建学校', school_delete: '删除学校',
    status_change: '状态变更', info_update: '信息更新',
    module_change: '模块变更', batch_module_change: '批量模块变更',
  };

  if (loading) return <Card><Spin spinning><div style={{ height: 300 }} /></Spin></Card>;
  if (error) return <Card><Alert message={error} type="error" showIcon /></Card>;
  if (!school) return <Card><Empty description="学校不存在" /></Card>;

  const statusCfg = STATUS_CONFIG[school.status] || STATUS_CONFIG.active;
  const planCfg = PLAN_CONFIG[school.plan_type] || PLAN_CONFIG.free;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* 顶部信息卡 */}
      <Card
        title={
          <Space>
            <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/schools')} style={{ padding: 0 }} />
            <span>学校详情</span>
            <Badge status={statusCfg.badge} text={statusCfg.label} />
            <Tag color={planCfg.color}>{planCfg.label}</Tag>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/schools/${id}/edit`)}
            >
              编辑信息
            </Button>
            <Button
              icon={school.status === 'inactive' ? <CheckCircleOutlined /> : <StopOutlined />}
              type={school.status === 'inactive' ? 'primary' : 'default'}
              danger={school.status !== 'inactive'}
              onClick={() => {
                statusForm.setFieldsValue({
                  status: school.status,
                  expire_date: school.expire_date ? dayjs(school.expire_date) : null
                });
                setStatusModalOpen(true);
              }}
            >
              状态管理
            </Button>
          </Space>
        }
      >
        <Row gutter={[24, 0]}>
          <Col span={14}>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="学校名称" span={2}>
                <Text strong>{school.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="学校ID">{school.id}</Descriptions.Item>
              <Descriptions.Item label="联系人">{school.principal || <Text type="secondary">未设置</Text>}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{school.phone || <Text type="secondary">未设置</Text>}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{school.email || <Text type="secondary">未设置</Text>}</Descriptions.Item>
              <Descriptions.Item label="所在地区" span={2}>
                {formatAreaText(school.province, school.city, school.county) || <Text type="secondary">未规划 / 未完整</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="地址" span={2}>{school.address || <Text type="secondary">未设置</Text>}</Descriptions.Item>
              <Descriptions.Item label="到期日期">
                {school.expire_date ?
                  <Text type={dayjs(school.expire_date).isBefore(dayjs()) ? 'danger' : undefined}>
                    {dayjs(school.expire_date).format('YYYY-MM-DD')}
                  </Text> :
                  <Tag color="green">永久有效</Tag>
                }
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {school.created_at ? dayjs(school.created_at).format('YYYY-MM-DD HH:mm') : '-'}
              </Descriptions.Item>
              {school.notes && (
                <Descriptions.Item label="备注" span={2}>
                  <Text type="secondary">{school.notes}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Col>
          <Col span={10}>
            {stats && (
              <Row gutter={[12, 12]}>
                {[
                  { title: '班级数', value: stats.classCount, suffix: `/ ${school.max_classes || 50}`, color: '#1677ff' },
                  { title: '教师数', value: stats.teacherCount, suffix: `/ ${school.max_teachers || 200}`, color: '#52c41a' },
                  { title: '学生数', value: stats.studentCount, color: '#722ed1' },
                  { title: '已开模块', value: stats.moduleEnabledCount, suffix: '个', color: '#fa8c16' },
                  { title: '通讯录', value: stats.contactCount, color: '#13c2c2' },
                  { title: '广播消息', value: stats.messageCount, color: '#eb2f96' },
                ].map(item => (
                  <Col span={8} key={item.title}>
                    <Card size="small" style={{ textAlign: 'center', background: '#fafafa' }}>
                      <Statistic
                        title={<span style={{ fontSize: 12 }}>{item.title}</span>}
                        value={item.value || 0}
                        suffix={item.suffix}
                        valueStyle={{ color: item.color, fontSize: 20 }}
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Col>
        </Row>
      </Card>

      {/* 标签页 */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            key: 'modules',
            label: <span><AppstoreOutlined /> 模块管理</span>,
            children: (
              <div>
                <Alert
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="内测阶段：该学校默认开放全部模块"
                  description="当前不启用学校级套餐、积分或模块授权限制。这里仅展示内测开放范围，后期接入正式授权表后再开放开关、到期时间和额度配置。"
                />
                {Object.entries(CATEGORY_LABELS).map(([cat, catCfg]) => {
                  const catModules = INTERNAL_TEST_MODULES.filter((module) => module.category === cat);
                  if (catModules.length === 0) return null;
                  return (
                    <div key={cat} style={{ marginBottom: 24 }}>
                      <div style={{
                        marginBottom: 12,
                        padding: '8px 12px',
                        background: catCfg.color,
                        border: `1px solid ${catCfg.border}`,
                        borderRadius: 6
                      }}>
                        <Title level={5} style={{ margin: 0, color: catCfg.text }}>
                          {catCfg.label}
                        </Title>
                      </div>
                      <Row gutter={[12, 12]}>
                        {catModules.map((mod) => (
                          <Col xs={24} md={12} xl={8} key={mod.module_code}>
                            <div style={{ border: `1px solid ${catCfg.border}`, borderRadius: 6, padding: 12, minHeight: 96 }}>
                              <Space align="start">
                                <span style={{ fontSize: 20, color: catCfg.text }}>
                                  {MODULE_ICON_MAP[mod.module_code] || <AppstoreOutlined />}
                                </span>
                                <Space direction="vertical" size={4}>
                                  <Space>
                                    <Text strong>{mod.module_name}</Text>
                                    <Tag color="green">内测开放</Tag>
                                  </Space>
                                  <Text type="secondary">{mod.description}</Text>
                                </Space>
                              </Space>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  );
                })}
              </div>
            ),
          },
          ...(isSuperAdmin() ? [{ key: 'administrators', label: '管理员设置', children: <SchoolAdministratorSettings key={id} schoolId={id} /> }] : []),
          {
            key: 'classes',
            label: `班级列表 (${classes.length})`,
            children: (
              <Table
                columns={classColumns}
                dataSource={classes}
                rowKey="id"
                size="small"
                pagination={{ pageSize: 10 }}
              />
            ),
          },
          {
            key: 'teachers',
            label: `教师列表 (${teachers.length})`,
            children: (
              <Table
                columns={teacherColumns}
                dataSource={teachers}
                rowKey="id"
                size="small"
                pagination={{ pageSize: 10 }}
              />
            ),
          },
          {
            key: 'logs',
            label: <span><HistoryOutlined /> 操作日志</span>,
            children: (
              <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                {logs.length === 0 ? <Empty description="暂无操作日志" /> : (
                  <Timeline
                    items={logs.map(log => ({
                      color: log.operation_type === 'status_change' ? 'blue' :
                             log.operation_type.includes('delete') ? 'red' : 'green',
                      children: (
                        <div>
                          <Text strong>{operationTypeLabels[log.operation_type] || log.operation_type}</Text>
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                            {dayjs(log.created_at).format('YYYY-MM-DD HH:mm:ss')}
                          </Text>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              操作人：{log.operator_name || '系统'}
                              {log.before_value && log.after_value &&
                                `  |  ${log.before_value} → ${log.after_value}`}
                            </Text>
                          </div>
                        </div>
                      )
                    }))}
                  />
                )}
              </div>
            ),
          },
        ]} />
      </Card>

      {/* 状态管理对话框 */}
      <Modal
        title="学校状态管理"
        open={statusModalOpen}
        onCancel={() => setStatusModalOpen(false)}
        onOk={() => statusForm.submit()}
        okText="确认更新"
      >
        <Alert
          message="状态说明"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><Text strong>正常运营</Text>：学校所有功能正常可用</li>
              <li><Text strong>试用中</Text>：学校处于试用期，功能可用但有限制</li>
              <li><Text strong>已停用</Text>：学校功能全部不可用，教师/学生无法登录</li>
              <li><Text strong>已到期</Text>：订阅已过期，需要续费才能恢复</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={statusForm} layout="vertical" onFinish={handleStatusChange}>
          <Form.Item name="status" label="学校状态" rules={[{ required: true }]}>
            <Select>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <Option key={k} value={k}>
                  <Badge status={v.badge} text={v.label} />
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="expire_date" label="到期日期（留空表示永久有效）">
            <DatePicker style={{ width: '100%' }} placeholder="选择到期日期（可选）" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default SchoolDetail;
